// The plane narrowing every board backend shares.
//
// v0.9.0 §7: a member declared inside a partial-presence block (`on <plane>
// { … }`) is ABSENT from primary. Nothing hides it on an unprojected board, so
// a model carrying any such member gets an implicit `on primary` — the SAME
// narrowing path an explicit plane pick takes, so the default board shows
// exactly the primary-plane model.
//
// Both backends read this module so they cannot drift: the live shell composes
// the plane onto the active view's clauses (app-shell `viewPlan.ts`), and the
// headless board (`board-render.ts` → CLI / MCP / server) plans the plane
// standalone. A model that uses no partial presence gets `null` from both —
// the identity board, untouched, by reference.

import {
  buildClosure,
  buildNodeUniverse,
  planView,
  PRIMARY_PLANE,
  type DependencyGraph,
  type EvalContext,
  type ResolvedModel,
  type ViewClause,
  type ViewDeclV2,
  type ViewProjectionPlan,
} from "@archlang/engine";

/**
 * The plane a board should project, given what the caller asked for. An
 * explicit ask always wins; otherwise a model carrying plane-exclusive members
 * falls back to `primary`, and every other model to `null` (identity board).
 */
export function effectivePlane(
  model: Pick<ResolvedModel, "presence"> | null,
  requested: string | null = null,
): string | null {
  if (requested) return requested;
  return model?.presence?.planeExclusiveMembers.size ? "primary" : null;
}

/**
 * Why a requested plane can't be projected, or null when it can.
 *
 * A typo'd plane narrows to nothing and renders an EMPTY board — the engine
 * raises `VIEW_UNKNOWN_PLANE`, but a standalone plane pick carries no view to
 * hang diagnostics off, so every headless caller (CLI / MCP / server) would
 * write a blank diagram and exit 0. They ask this first and refuse instead.
 * `primary` is always projectable: it is the implicit structural plane.
 */
export function planeRejection(
  model: Pick<ResolvedModel, "presence">,
  requested: string | null | undefined,
): string | null {
  if (!requested || requested === PRIMARY_PLANE) return null;
  const known = model.presence?.planes ?? [];
  if (known.includes(requested)) return null;
  return known.length
    ? `unknown plane "${requested}" — this workspace declares ${known.map((p) => `"${p}"`).join(", ")}`
    : `unknown plane "${requested}" — this workspace declares no planes`;
}

/**
 * The `defaultPlane` half of an `EvalContext`, for a surface that plans a
 * DECLARED view (table / index / matrix) rather than a bare board. Spread into
 * the context so the headless surface narrows exactly as the live one does; the
 * engine ignores it for a view that names a plane of its own.
 */
export function planeDefaultCtx(
  model: Pick<ResolvedModel, "presence">,
): { readonly defaultPlane?: string } {
  const plane = effectivePlane(model);
  return plane ? { defaultPlane: plane } : {};
}

/** An `on <plane>` clause — the one construction site for the synthetic pick. */
export function planeClause(plane: string): ViewClause {
  return { kind: "on", planes: [plane] } as ViewClause;
}

/**
 * The projection plan for a standalone plane pick (no declared view involved) —
 * what the headless board backends use. `null` when nothing needs narrowing.
 */
export function planePlan(
  model: ResolvedModel,
  graph: DependencyGraph,
  requested: string | null = null,
): ViewProjectionPlan | null {
  const plane = effectivePlane(model, requested);
  if (!plane) return null;
  const view: ViewDeclV2 = {
    name: "__plane__",
    header: { fields: [], knobs: [] },
    // An explicit ask is a plane PICK — `on <plane>`, which narrows nodes and
    // members alike. The implicit default carries no clause: the engine hides
    // what a partial-presence block declared and leaves the rest of the model
    // (a plane-scoped module included) exactly where it was.
    clauses: requested ? [planeClause(requested)] : [],
  } as ViewDeclV2;
  const ctx: EvalContext = {
    universe: buildNodeUniverse(model),
    graph,
    closure: buildClosure(graph),
    presence: model.presence,
    ...(requested ? {} : { defaultPlane: plane }),
  };
  return planView(view, ctx);
}
